import axios from "axios";
import * as Sentry from "@sentry/react";

const { REACT_APP_API_URL: API_URL } = process.env;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 60 second timeout
});

// Helper to check if error is a network error (no response received)
export const isNetworkError = (error) => {
  return (
    error.code === "ERR_NETWORK" ||
    error.code === "ECONNABORTED" ||
    error.message === "Network Error" ||
    !error.response
  );
};

// Store active spans keyed by request config
// Using WeakMap so spans are garbage collected when requests complete
const requestSpans = new WeakMap();

// Request interceptor - creates Sentry span and starts timing
api.interceptors.request.use(
  (config) => {
    // Extract meaningful operation name from URL
    const url = new URL(config.url, config.baseURL || API_URL);
    const operationName = `${config.method.toUpperCase()} ${url.pathname}`;

    // Start a new Sentry span for this request
    const span = Sentry.startInactiveSpan({
      name: operationName,
      op: "http.client",
      attributes: {
        "http.method": config.method.toUpperCase(),
        "http.url": url.pathname,
        "http.query": url.search,
      },
    });

    // Store the span so we can access it in the response interceptor
    if (span) {
      requestSpans.set(config, span);
    }

    return config;
  },
  (error) => {
    // Handle request setup errors
    Sentry.captureException(error, {
      contexts: { custom: { message: "Request setup failed" } },
    });
    return Promise.reject(error);
  }
);

// Response interceptor - finishes span and logs metrics
api.interceptors.response.use(
  (response) => {
    const span = requestSpans.get(response.config);

    if (span) {
      // Add response metadata to span
      span.setAttributes({
        "http.status_code": response.status,
        "http.response_content_length": response.headers["content-length"],
      });

      // Mark span as successful
      span.setStatus({ code: 1 }); // OK status

      // End the span (this captures the duration automatically)
      span.end();

      // Clean up the WeakMap entry
      requestSpans.delete(response.config);
    }

    return response;
  },
  (error) => {
    const span = requestSpans.get(error.config);
    const isNetwork = isNetworkError(error);

    // Log network errors with more context for debugging
    if (isNetwork) {
      Sentry.logger.warn("Network error occurred", {
        url: error.config?.url,
        method: error.config?.method,
        code: error.code,
        message: error.message,
        online: navigator.onLine,
      });

      // Add breadcrumb for network errors
      Sentry.addBreadcrumb({
        category: "network",
        message: `Network error: ${error.message}`,
        level: "warning",
        data: {
          url: error.config?.url,
          online: navigator.onLine,
        },
      });
    }

    if (span) {
      // Add error metadata to span
      const status = error.response?.status;

      span.setAttributes({
        "http.status_code": status,
        "error.type": error.name,
        "error.message": error.message,
        "error.is_network": isNetwork,
        "browser.online": navigator.onLine,
      });

      // Mark span as failed
      span.setStatus({ code: 2, message: error.message }); // ERROR status

      // End the span
      span.end();

      // Clean up the WeakMap entry
      requestSpans.delete(error.config);
    }

    return Promise.reject(error);
  }
);

// Helper function for creating custom spans for non-HTTP operations
export const withSpan = async (spanOptions, callback) => {
  return await Sentry.startSpan(spanOptions, callback);
};

// Helper function to add attributes to the current active span
export const addSpanAttributes = (attributes) => {
  const span = Sentry.getActiveSpan();
  if (span) {
    span.setAttributes(attributes);
  }
};

export default api;
